// ============= Imports =============
import React, { useMemo } from 'react';
import { Button, Modal } from '@/ui';
import { ErrorBoundary } from '@/components/common/ErrorBoundary/ErrorBoundary';
import { VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/messages';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { useVisualPromptWizard } from '@/hooks/visualPrompts/useVisualPromptWizard';
import { buildReviewContext } from '@/utils/visualPrompts/buildReviewContext';
import { saveChoicesForPrompt } from '@/utils/visualPrompts/visualPromptStorage';
import { renderWizardStep } from '../../VisualPromptWizard/renderWizardStep';
import type { VisualPromptWizardProps } from '../../VisualPromptWizard/types';
import {
  Footer,
  FooterActions,
  ModalBody,
  PreviewPanel,
  PreviewText,
  StepContent,
  StepIndicator,
  StepLabel,
  StepTitle,
} from './styles';

// ============= Component =============
export const CompactVisualPromptWizard: React.FC<VisualPromptWizardProps> = ({
  isOpen,
  definition,
  onClose,
  onRun,
  isRunning = false,
  reviewContext: reviewContextBase,
}) => {
  const wizard = useVisualPromptWizard(definition);
  const { handleSuccess } = useWorkspaceErrorHandler();

  const reviewContext = useMemo(
    () =>
      buildReviewContext(wizard.answers, reviewContextBase?.workspaceName),
    [wizard.answers, reviewContextBase?.workspaceName],
  );

  const handleSaveChoices = () => {
    if (!definition) {
      return;
    }
    saveChoicesForPrompt(definition.id, wizard.answers);
    handleSuccess(VISUAL_PROMPT_MESSAGES.saveChoicesSuccess);
  };

  const handleClose = () => {
    onClose();
  };

  const handlePrimary = async () => {
    if (!definition) {
      return;
    }
    if (wizard.isReviewStep || wizard.isLastStep) {
      await onRun(definition, wizard.answers);
      return;
    }
    wizard.goNext();
  };

  const stepNumber = wizard.currentStepIndex + 1;
  const stepTotal = wizard.visibleSteps.length;
  const currentTitle = wizard.currentStep?.title ?? '';

  const primaryLabel =
    wizard.isReviewStep || wizard.isLastStep
      ? VISUAL_PROMPT_MESSAGES.runPrompt
      : VISUAL_PROMPT_MESSAGES.continue;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={definition?.title ?? 'Guided prompt'}
      size="large"
      footer={
        <Footer>
          <Button
            variant="ghost"
            onClick={wizard.currentStepIndex > 0 ? wizard.goBack : handleClose}
            disabled={isRunning}
          >
            {wizard.currentStepIndex > 0
              ? VISUAL_PROMPT_MESSAGES.back
              : 'Cancel'}
          </Button>
          <FooterActions>
            <Button
              variant="primary"
              onClick={() => void handlePrimary()}
              disabled={isRunning || !wizard.canProceed()}
            >
              {isRunning
                ? VISUAL_PROMPT_MESSAGES.runningPrompt
                : primaryLabel}
            </Button>
          </FooterActions>
        </Footer>
      }
    >
      <ErrorBoundary>
        <ModalBody>
          {definition && wizard.currentStep && (
            <>
              <StepIndicator>
                <StepLabel>
                  Step {stepNumber} of {stepTotal}
                </StepLabel>
                <StepTitle>{currentTitle}</StepTitle>
              </StepIndicator>
              <StepContent>
                {renderWizardStep(
                  wizard.currentStep,
                  wizard.answers,
                  wizard.setAnswer,
                  wizard.stepError,
                  wizard.compiledPreview,
                  {
                    definition,
                    reviewContext,
                    onSaveChoices: handleSaveChoices,
                  },
                )}
              </StepContent>
              {wizard.currentStep.type !== 'review' && (
                <PreviewPanel>
                  <summary>{VISUAL_PROMPT_MESSAGES.previewTitle}</summary>
                  <PreviewText>
                    {wizard.compiledPreview || '(builds as you answer)'}
                  </PreviewText>
                </PreviewPanel>
              )}
            </>
          )}
        </ModalBody>
      </ErrorBoundary>
    </Modal>
  );
};
